// Plan-1.9 Phase 3.B.CP1 — Open-Graph-Anchor URL-Parser.
//
// Master-Briefing v5 Z. 384-386 spezifiziert das URL-Schema:
//   Primär: Hash-Anchor   `viewer.scrolly.com/scrollytelling/<slug>#scene-3`
//   Fallback: Query-Param `viewer.scrolly.com/scrollytelling/<slug>?at=scene-3`
//                          (für Bots, die Hash droppen)
//
// Hash hat Vorrang über Query — Sharer-Intent (`#scene-N` direkt im
// Tweet/LinkedIn-Link) ist intentionaler als ein Bot-Fallback-Param,
// und Bot-OG-Render erreicht den Frontend-Pfad sowieso nicht
// (nginx-Bot-Detection routet Bots zum Backend-OG-Endpoint, siehe
// docs/PHASE-3-PRE-VERIFIKATION-2026-05-10.md Sektion B).
//
// Scene-Index ist **1-indexed** in der URL (`#scene-1` = erstes Slide),
// konsistent mit User-Intuition und mit dem ResumeToast-Wording
// "Weiterlesen ab Slide X" aus Sub-Phase 3.A. Konvertierung zur
// 0-indexed Internal-Repräsentation (slidePositions-Array,
// VisualStoryStore.goToSlide-Argument) passiert im Konsumenten —
// dieser Parser liefert den raw 1-indexed Wert.
//
// Out-of-Range-Werte (z.B. `#scene-99` bei einem Scrolly mit 5
// Slides) werden hier NICHT geprüft, weil der Parser das totalSlides
// nicht kennt. Master-Briefing v5 Z. 407 ("Fallback zur Scrolly-
// Anfang") wird im Renderer-Effect (CP2) durch ein no-op (kein
// goToSlide) bzw. Clamp realisiert. Parser liefert den raw idx.

export type SceneAnchorSource = "hash" | "query";

export interface SceneAnchor {
  /** 1-indexed Scene-Index aus URL. Garantiert >= 1, Integer, finite. */
  sceneIdxOneIndexed: number;
  /** Welche URL-Komponente die Quelle war (für Debug/Telemetry). */
  source: SceneAnchorSource;
}

/**
 * Liest einen Scene-Anchor aus Vike's pageContext.urlParsed-Komponenten.
 *
 * @param hash Vike-`urlParsed.hash` — String ohne führendes `#` per
 *             Vike-Konvention, der Parser akzeptiert aber beide
 *             Formate (`scene-3` und `#scene-3`) für Robustheit.
 * @param search Vike-`urlParsed.search` — Query-Map; nur das Feld `at`
 *               wird konsultiert (Master-Briefing v5 Z. 386).
 * @returns Resolved Scene-Anchor mit Quelle, oder null wenn weder
 *          Hash noch Query einen valid 1-indexed Scene-Anchor enthalten.
 */
export function parseSceneAnchor(
  hash: string | undefined | null,
  search: Record<string, string> | undefined,
): SceneAnchor | null {
  const hashIdx = parseSceneToken(hash);
  if (hashIdx !== null) {
    return { sceneIdxOneIndexed: hashIdx, source: "hash" };
  }
  const queryIdx = parseSceneToken(search?.at);
  if (queryIdx !== null) {
    return { sceneIdxOneIndexed: queryIdx, source: "query" };
  }
  return null;
}

/**
 * Parsed `scene-N` (oder `#scene-N`) zu 1-indexed Integer.
 * Liefert null bei jeglicher Form von Invalid-Input — kein Throw.
 */
function parseSceneToken(token: string | undefined | null): number | null {
  if (!token) return null;
  const cleaned = token.startsWith("#") ? token.slice(1) : token;
  // Regex matcht NUR `scene-` gefolgt von 1+ ASCII-Digits bis EOS.
  // Damit fallen "scene-foo", "scene-1.5", "scene--1", "scene-1e10",
  // "scene- 3" (Whitespace), "scene-3 " (trailing-Whitespace) etc. raus.
  const match = /^scene-(\d+)$/.exec(cleaned);
  if (!match) return null;
  const n = Number.parseInt(match[1], 10);
  // Defensive: parseInt kann theoretisch NaN bei extremen Inputs liefern;
  // plus 1-indexed-Constraint (`#scene-0` ist nicht gültig — User-URL
  // beginnt bei `#scene-1`).
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}
